import { useState, useEffect, useCallback } from 'react';
import {
  fetchGiftCodes,
  createGiftCode,
  toggleGiftCodeStatus,
  type GiftCode,
} from '@/lib/supabase';

function generateCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

// Common preset code name suggestions
const CODE_PRESETS = [
  'WELCOME50', 'DIWALI100', 'HOLI2025', 'SAVE20', 'FLAT100',
  'NEWUSER', 'SPECIAL', 'FESTIVE', 'DISCOUNT', 'OFFER50',
];

export default function GiftCodesPanel() {
  const [codes, setCodes] = useState<GiftCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [codeMode, setCodeMode] = useState<'custom' | 'random'>('custom');

  const [form, setForm] = useState({
    code: '',
    customName: '',
    discount_type: 'flat' as 'flat' | 'percent',
    discount_value: 50,
    min_order_amount: 0,
    max_uses: 1,
    is_active: true,
    expires_at: '',
    description: '',
  });

  const showToast = useCallback((msg: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadCodes = useCallback(async () => {
    setLoading(true);
    const data = await fetchGiftCodes();
    setCodes(data);
    setLoading(false);
  }, []);

  useEffect(() => { loadCodes(); }, [loadCodes]);

  // Copy to clipboard
  const handleCopy = useCallback(async (code: GiftCode) => {
    try {
      await navigator.clipboard.writeText(code.code);
      setCopiedId(code.id);
      showToast(`"${code.code}" copy ho gaya!`, 'success');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Fallback for older browsers
      const el = document.createElement('textarea');
      el.value = code.code;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopiedId(code.id);
      showToast(`"${code.code}" copy ho gaya!`, 'success');
      setTimeout(() => setCopiedId(null), 2000);
    }
  }, [showToast]);

  const getFinalCode = () => {
    if (codeMode === 'custom' && form.customName.trim()) {
      return form.customName.toUpperCase().replace(/\s+/g, '').slice(0, 20);
    }
    return form.code;
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const finalCode = getFinalCode();
    if (!finalCode) { showToast('Code daalo ya generate karo', 'error'); return; }
    if (form.discount_value <= 0) { showToast('Discount value 0 se zyada hona chahiye', 'error'); return; }
    if (form.discount_type === 'percent' && form.discount_value > 100) { showToast('Percent 100 se zyada nahi ho sakta', 'error'); return; }

    setSaving(true);
    const result = await createGiftCode({
      code: finalCode,
      discount_type: form.discount_type,
      discount_value: form.discount_value,
      min_order_amount: form.min_order_amount,
      max_uses: form.max_uses,
      is_active: form.is_active,
      expires_at: form.expires_at || undefined,
      description: form.description,
    });
    setSaving(false);

    if (result.success) {
      showToast(`Gift code "${finalCode}" ban gaya!`, 'success');
      setShowCreate(false);
      setForm({ code: '', customName: '', discount_type: 'flat', discount_value: 50, min_order_amount: 0, max_uses: 1, is_active: true, expires_at: '', description: '' });
      setCodeMode('custom');
      loadCodes();
    } else {
      showToast(result.error ?? 'Error aa gaya', 'error');
    }
  };

  const handleToggle = async (code: GiftCode) => {
    setToggling(code.id);
    await toggleGiftCodeStatus(code.id, !code.is_active);
    setCodes(prev => prev.map(c => c.id === code.id ? { ...c, is_active: !c.is_active } : c));
    setToggling(null);
    showToast(code.is_active ? 'Code deactivate ho gaya' : 'Code active ho gaya', 'info');
  };

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const activeCodes = codes.filter(c => c.is_active);
  const inactiveCodes = codes.filter(c => !c.is_active);
  const previewCode = getFinalCode() || 'CODE';

  return (
    <div className="space-y-5">
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-2xl text-white text-sm font-semibold flex items-center gap-2 whitespace-nowrap shadow-xl ${
          toast.type === 'success' ? 'bg-green-500' : toast.type === 'error' ? 'bg-red-500' : 'bg-gray-800'
        }`}>
          <i className={toast.type === 'success' ? 'ri-check-line' : toast.type === 'error' ? 'ri-close-line' : 'ri-information-line'}></i>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center">
              <i className="ri-coupon-3-line text-white text-lg"></i>
            </div>
            <div>
              <h2 className="text-gray-900 font-bold text-lg">Gift Codes</h2>
              <p className="text-gray-400 text-xs">{activeCodes.length} active · {codes.length} total</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => loadCodes()}
              className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 cursor-pointer transition-all"
            >
              <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
            </button>
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-900 text-white font-bold text-sm cursor-pointer hover:bg-gray-800 transition-all whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              New Gift Code
            </button>
          </div>
        </div>
      </div>

      {/* Create Form Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-pink-500 to-rose-600 px-6 py-5 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <i className="ri-coupon-3-fill text-white text-lg"></i>
                </div>
                <div>
                  <p className="text-white font-bold text-base">Naya Gift Code Banao</p>
                  <p className="text-white/70 text-xs">Customer ke liye discount code</p>
                </div>
              </div>
              <button
                onClick={() => setShowCreate(false)}
                className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center text-white cursor-pointer hover:bg-white/30"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <form onSubmit={handleCreate} className="flex-1 overflow-y-auto p-6 space-y-4">

              {/* Code Mode Toggle */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Code Type</label>
                <div className="grid grid-cols-2 gap-2 bg-gray-100 rounded-xl p-1">
                  <button
                    type="button"
                    onClick={() => setCodeMode('custom')}
                    className={`py-2.5 rounded-lg text-xs font-bold cursor-pointer transition-all ${codeMode === 'custom' ? 'bg-white text-gray-900 shadow' : 'text-gray-500'}`}
                  >
                    <i className="ri-edit-line mr-1"></i>Custom Name
                  </button>
                  <button
                    type="button"
                    onClick={() => { setCodeMode('random'); setForm(p => ({ ...p, code: generateCode() })); }}
                    className={`py-2.5 rounded-lg text-xs font-bold cursor-pointer transition-all ${codeMode === 'random' ? 'bg-white text-gray-900 shadow' : 'text-gray-500'}`}
                  >
                    <i className="ri-shuffle-line mr-1"></i>Random Code
                  </button>
                </div>
              </div>

              {/* Custom Name Input */}
              {codeMode === 'custom' && (
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                    Custom Code Name
                  </label>
                  <input
                    type="text"
                    value={form.customName}
                    onChange={e => setForm(p => ({ ...p, customName: e.target.value.toUpperCase().replace(/\s+/g, '') }))}
                    placeholder="e.g. DIWALI100, SAVE50, WELCOME..."
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm font-mono font-bold tracking-widest focus:border-pink-400 focus:ring-2 focus:ring-pink-400/20 outline-none uppercase"
                    maxLength={20}
                  />
                  <p className="text-gray-400 text-xs mt-1">Max 20 characters, no spaces allowed</p>

                  {/* Quick Preset Suggestions */}
                  <div className="mt-2">
                    <p className="text-gray-400 text-[11px] mb-1.5">Quick suggestions:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {CODE_PRESETS.map(preset => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => setForm(p => ({ ...p, customName: preset }))}
                          className={`px-2.5 py-1 rounded-lg text-[11px] font-bold cursor-pointer transition-all whitespace-nowrap ${
                            form.customName === preset
                              ? 'bg-pink-500 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-pink-50 hover:text-pink-600'
                          }`}
                        >
                          {preset}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Random Code Input */}
              {codeMode === 'random' && (
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Generated Code</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={form.code}
                      onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                      placeholder="AUTO GENERATED"
                      className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-sm font-mono font-bold tracking-widest focus:border-pink-400 focus:ring-2 focus:ring-pink-400/20 outline-none uppercase"
                      maxLength={20}
                    />
                    <button
                      type="button"
                      onClick={() => setForm(p => ({ ...p, code: generateCode() }))}
                      className="px-3 py-3 rounded-xl bg-gray-100 text-gray-600 text-sm hover:bg-gray-200 cursor-pointer transition-all"
                      title="Generate new random code"
                    >
                      <i className="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
              )}

              {/* Discount Type */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Discount Type</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setForm(p => ({ ...p, discount_type: 'flat' }))}
                    className={`py-3 rounded-xl text-sm font-bold cursor-pointer transition-all border-2 ${form.discount_type === 'flat' ? 'bg-gray-900 text-white border-gray-900' : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-400'}`}
                  >
                    <i className="ri-money-rupee-circle-line mr-1"></i>Flat ₹
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(p => ({ ...p, discount_type: 'percent' }))}
                    className={`py-3 rounded-xl text-sm font-bold cursor-pointer transition-all border-2 ${form.discount_type === 'percent' ? 'bg-gray-900 text-white border-gray-900' : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-400'}`}
                  >
                    <i className="ri-percent-line mr-1"></i>Percent %
                  </button>
                </div>
              </div>

              {/* Discount Value */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                  {form.discount_type === 'flat' ? 'Discount Amount (₹)' : 'Discount Percentage (%)'}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">
                    {form.discount_type === 'flat' ? '₹' : '%'}
                  </span>
                  <input
                    type="number"
                    value={form.discount_value}
                    onChange={e => setForm(p => ({ ...p, discount_value: Number(e.target.value) }))}
                    min={1}
                    max={form.discount_type === 'percent' ? 100 : 10000}
                    className="w-full pl-9 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-pink-400 focus:ring-2 focus:ring-pink-400/20 outline-none"
                  />
                </div>
              </div>

              {/* Min Order + Max Uses */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Min Order (₹)</label>
                  <input
                    type="number"
                    value={form.min_order_amount}
                    onChange={e => setForm(p => ({ ...p, min_order_amount: Number(e.target.value) }))}
                    min={0}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-pink-400 focus:ring-2 focus:ring-pink-400/20 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Max Uses</label>
                  <input
                    type="number"
                    value={form.max_uses}
                    onChange={e => setForm(p => ({ ...p, max_uses: Number(e.target.value) }))}
                    min={1}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-pink-400 focus:ring-2 focus:ring-pink-400/20 outline-none"
                  />
                </div>
              </div>

              {/* Expiry */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Expiry Date (Optional)</label>
                <input
                  type="date"
                  value={form.expires_at}
                  onChange={e => setForm(p => ({ ...p, expires_at: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-pink-400 focus:ring-2 focus:ring-pink-400/20 outline-none"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Description (Optional)</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="e.g. Diwali Special Offer"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-pink-400 focus:ring-2 focus:ring-pink-400/20 outline-none"
                />
              </div>

              {/* Live Preview */}
              <div className="bg-gradient-to-br from-pink-50 to-rose-50 border border-pink-200 rounded-2xl p-4">
                <p className="text-pink-800 font-bold text-xs uppercase tracking-wide mb-3 flex items-center gap-1">
                  <i className="ri-eye-line"></i> Live Preview
                </p>
                <div className="bg-white rounded-xl p-3 border border-pink-200 flex items-center gap-3">
                  <div className="border-2 border-dashed border-pink-400 rounded-xl px-4 py-2.5 text-center min-w-0">
                    <p className="font-mono font-bold text-pink-700 text-base tracking-widest leading-none">{previewCode}</p>
                    <p className="text-pink-400 text-[10px] mt-0.5">Gift Code</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 font-bold text-sm">
                      {form.discount_type === 'flat' ? `₹${form.discount_value}` : `${form.discount_value}%`} OFF
                    </p>
                    {form.min_order_amount > 0 && <p className="text-gray-400 text-xs">Min order ₹{form.min_order_amount}</p>}
                    <p className="text-gray-400 text-xs">{form.max_uses} use{form.max_uses > 1 ? 's' : ''} max</p>
                    {form.description && <p className="text-pink-600 text-xs font-medium truncate">{form.description}</p>}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-gray-600 font-bold text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || !previewCode || previewCode === 'CODE'}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-pink-500 to-rose-600 text-white font-bold text-sm cursor-pointer hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  {saving ? <><i className="ri-loader-4-line animate-spin"></i>Saving...</> : <><i className="ri-coupon-3-fill"></i>Create Code</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Codes List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-100">
          <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-pink-500 animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading codes...</p>
        </div>
      ) : codes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-100">
          <div className="w-16 h-16 rounded-2xl bg-pink-50 flex items-center justify-center mb-4">
            <i className="ri-coupon-3-line text-3xl text-pink-300"></i>
          </div>
          <p className="text-gray-700 font-semibold">Koi gift code nahi hai</p>
          <p className="text-gray-400 text-sm mt-1">Upar "New Gift Code" button se banao</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activeCodes.length > 0 && (
            <>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1">Active Codes ({activeCodes.length})</p>
              {activeCodes.map(code => (
                <CodeCard key={code.id} code={code} onToggle={handleToggle} onCopy={handleCopy} toggling={toggling} copiedId={copiedId} formatDate={formatDate} />
              ))}
            </>
          )}
          {inactiveCodes.length > 0 && (
            <>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1 pt-2">Inactive Codes ({inactiveCodes.length})</p>
              {inactiveCodes.map(code => (
                <CodeCard key={code.id} code={code} onToggle={handleToggle} onCopy={handleCopy} toggling={toggling} copiedId={copiedId} formatDate={formatDate} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CodeCard({ code, onToggle, onCopy, toggling, copiedId, formatDate }: {
  code: GiftCode;
  onToggle: (c: GiftCode) => void;
  onCopy: (c: GiftCode) => void;
  toggling: string | null;
  copiedId: string | null;
  formatDate: (s?: string) => string;
}) {
  const usagePercent = code.max_uses > 0 ? Math.min(100, (code.used_count / code.max_uses) * 100) : 0;
  const isExpired = code.expires_at ? new Date(code.expires_at) < new Date() : false;
  const isCopied = copiedId === code.id;

  return (
    <div className={`bg-white rounded-2xl border p-4 transition-all ${!code.is_active || isExpired ? 'opacity-60 border-gray-100' : 'border-gray-100 hover:border-pink-200'}`}>
      <div className="flex items-start gap-3">
        {/* Code badge + copy */}
        <div className="shrink-0">
          <div className={`rounded-xl px-3 py-2 border-2 border-dashed mb-1.5 ${code.is_active && !isExpired ? 'border-pink-400 bg-pink-50' : 'border-gray-300 bg-gray-50'}`}>
            <p className={`font-mono font-bold text-sm tracking-widest ${code.is_active && !isExpired ? 'text-pink-700' : 'text-gray-400'}`}>{code.code}</p>
          </div>
          {/* Copy Button */}
          <button
            onClick={() => onCopy(code)}
            className={`w-full flex items-center justify-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold cursor-pointer transition-all whitespace-nowrap ${
              isCopied
                ? 'bg-green-100 text-green-700 border border-green-300'
                : 'bg-gray-100 text-gray-600 hover:bg-pink-50 hover:text-pink-600 border border-gray-200'
            }`}
          >
            <i className={isCopied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
            {isCopied ? 'Copied!' : 'Copy'}
          </button>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${code.discount_type === 'flat' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
              {code.discount_type === 'flat' ? `₹${code.discount_value} off` : `${code.discount_value}% off`}
            </span>
            {code.min_order_amount > 0 && (
              <span className="text-gray-400 text-xs">Min ₹{code.min_order_amount}</span>
            )}
            {isExpired && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-600 text-[10px] font-bold">EXPIRED</span>}
          </div>
          {code.description && <p className="text-gray-500 text-xs mt-0.5">{code.description}</p>}

          {/* Usage bar */}
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-400 text-[11px]">{code.used_count}/{code.max_uses} uses</span>
              <span className="text-gray-400 text-[11px]">Expires: {formatDate(code.expires_at)}</span>
            </div>
            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${usagePercent >= 100 ? 'bg-red-400' : usagePercent >= 70 ? 'bg-amber-400' : 'bg-green-400'}`}
                style={{ width: `${usagePercent}%` }}
              />
            </div>
          </div>
        </div>

        {/* Toggle */}
        <button
          onClick={() => onToggle(code)}
          disabled={toggling === code.id}
          className={`shrink-0 relative w-12 h-6 rounded-full transition-all cursor-pointer disabled:opacity-60 ${code.is_active ? 'bg-green-500' : 'bg-gray-300'}`}
        >
          {toggling === code.id ? (
            <span className="absolute inset-0 flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin text-white text-xs"></i>
            </span>
          ) : (
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${code.is_active ? 'translate-x-6' : 'translate-x-0'}`}></span>
          )}
        </button>
      </div>
    </div>
  );
}
